import { FC, useState, useEffect } from 'react';
import Select from 'react-select';

import { RosterRequestStatus } from '../../generated/graphql';
import {
  getStatusColor,
  getStatusTextColor,
} from '../Inputs/TableFilterInputs/ReactSelect/TableFilterReactSelectEnum';

interface StatusOption {
  label: string;
  value: RosterRequestStatus;
}

interface FilterChipStatusProps {
  label: string;
  value: RosterRequestStatus[];
  fieldName: string;
  onUpdate: (fieldName: string, value: RosterRequestStatus[]) => void;
  isEditing: boolean;
  onEditingChange: (editing: boolean) => void;
}

const statusOptions: StatusOption[] = [
  { label: 'Request Received', value: RosterRequestStatus.RequestReceived },
  { label: 'In Progress', value: RosterRequestStatus.InProgress },
  {
    label: 'Cancellation Requested',
    value: RosterRequestStatus.CancellationRequested,
  },
  {
    label: 'Awaiting Confirmation',
    value: RosterRequestStatus.AwaitingConfirmation,
  },
  { label: 'More Info Required', value: RosterRequestStatus.MoreInfoRequired },
  {
    label: 'Over Budget Approval Needed',
    value: RosterRequestStatus.OverPdApprovalNeeded,
  },
  { label: 'Confirmed', value: RosterRequestStatus.Confirmed },
  { label: 'Canceled', value: RosterRequestStatus.Canceled },
];

const FilterChipStatus: FC<FilterChipStatusProps> = ({
  label,
  value,
  fieldName,
  onUpdate,
  isEditing,
  onEditingChange,
}) => {
  const [localValue, setLocalValue] = useState<StatusOption[]>(
    value
      .map((status) => statusOptions.find((opt) => opt.value === status)!)
      .filter(Boolean),
  );

  // Synchronize localValue with prop changes (e.g., when filters are cleared)
  useEffect(() => {
    setLocalValue(
      value
        .map((status) => statusOptions.find((opt) => opt.value === status)!)
        .filter(Boolean),
    );
  }, [value]);

  const handleUpdate = () => {
    onUpdate(
      fieldName,
      localValue.map((option) => option.value),
    );
    onEditingChange(false);
  };

  const displayValue =
    value.length > 0
      ? value.length === statusOptions.length
        ? 'All'
        : value.length > 2
          ? `${value.length} selected`
          : value
              .map(
                (status) =>
                  statusOptions.find((opt) => opt.value === status)?.label,
              )
              .join(', ')
      : 'All';

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => onEditingChange(!isEditing)}
        className="inline-flex items-center gap-x-1 rounded-xl border border-gray-200 bg-lightGreen px-3 py-1 text-sm hover:bg-lightGreen"
      >
        <span className="font-medium">{label}:</span>
        <span className="max-w-32 truncate">{displayValue}</span>
      </button>

      {isEditing && (
        <div className="absolute left-0 top-full z-10 mt-2 min-w-[250px] rounded-xl border border-gray-200 bg-white p-4 shadow-lg">
          <div className="flex flex-col gap-y-4">
            <Select
              isMulti
              options={statusOptions}
              value={localValue}
              onChange={(newValue) => setLocalValue(newValue as StatusOption[])}
              styles={{
                multiValue: (base, { data }) => ({
                  ...base,
                  backgroundColor: getStatusColor(data.value),
                  borderRadius: '10px',
                  padding: '2px',
                  margin: '2px',
                }),
                multiValueLabel: (base, { data }) => ({
                  ...base,
                  color: getStatusTextColor(data.value),
                  fontSize: '12px',
                  padding: '1px',
                  fontWeight: '600',
                }),
                multiValueRemove: (base) => ({
                  ...base,
                  color: '#000000',
                  borderRadius: '10px',
                }),
              }}
            />
            <button
              type="button"
              onClick={handleUpdate}
              className="rounded-md bg-secondaryBlue px-4 py-2 text-sm font-medium text-white hover:bg-secondaryBlueShade"
            >
              Update
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterChipStatus;
